import { cloneDeep } from 'lodash';
import { RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { uuid } from 'short-uuid';

import { ServiceContractPosition } from '@work4all/models/lib/Classes/ServiceContractPosition.entity';
import { AddServiceContractPosTypeOfPos } from '@work4all/models/lib/Enums/AddServiceContractPosTypeOfPos.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

// ToDo: WW-7459 Move to shared shadow folder (2 below)
import { getIdsIncomingKeys } from '../../../../../../erp/components/tab-panels/positions/components/edit-table/hooks/getIdsIncomingKeys';
import { useTotalPrice } from '../../../../../../erp/components/tab-panels/positions/components/hooks/use-total-price';
import { ShadowServiceContractObjectAddComponentArgs } from '../../../../../hooks/use-shadow-service-contract-object-api';
import { useShadowServiceContractObjectApiContext } from '../../../../../hooks/use-shadow-service-contract-object-api-context';
import {
  useEditableState,
  UseEditableStateProps,
} from '../edit-table/hooks/use-editable-state';
import {
  EditTableEntry,
  IdArray,
  IEditTable,
  OnEditComponent,
} from '../edit-table/types';

import { recalculatePositions } from './recalculatePositions';

export type UsePostionState = UseEditableStateProps<
  ServiceContractPosition,
  ShadowServiceContractObjectAddComponentArgs
>;

interface UsePostionStateProps {
  editTableRef?: RefObject<IEditTable>;
  entity: Entities;
  isSimpleMode: boolean;
}

const POSITIONS_DEFAULT = [];
export const usePositionState = (props: UsePostionStateProps) => {
  const { editTableRef, entity, isSimpleMode } = props;
  const shadowServiceContractObjectApi =
    useShadowServiceContractObjectApiContext();

  const positions =
    shadowServiceContractObjectApi.positions || POSITIONS_DEFAULT;

  const { totalPrice, setTotalPrice } = useTotalPrice();

  const lastCacheId = useRef<string>(uuid());

  const mapAddLocal = useCallback(
    (context: ShadowServiceContractObjectAddComponentArgs) => {
      const { article } = context;
      const cacheOnly =
        context.typeOfPosition === AddServiceContractPosTypeOfPos.TEXTLINE &&
        context.article?.id === 0;

      const positionKind = article?.internalArticle
        ? AddServiceContractPosTypeOfPos.DEFAULT
        : context.typeOfPosition;

      // For HEADLINE and TEXTLINE, set numeric fields to null
      const isHeadlineOrTextline =
        positionKind === AddServiceContractPosTypeOfPos.HEADLINE ||
        positionKind === AddServiceContractPosTypeOfPos.TEXTLINE;

      const position: ServiceContractPosition = {
        id: article?.id,
        price: isHeadlineOrTextline ? null : 0,
        price2: isHeadlineOrTextline ? null : 0,
        servicePricePerYear: isHeadlineOrTextline ? null : 0,
        percent: isHeadlineOrTextline ? null : 0,
        discount: isHeadlineOrTextline ? null : 0,
        discount2: isHeadlineOrTextline ? null : 0,
        manufacturerNumber: isHeadlineOrTextline ? null : '0',
        purchasePrice: isHeadlineOrTextline ? null : 0,
        amountSupplier: isHeadlineOrTextline ? null : 0,
        amountDecimal: isHeadlineOrTextline ? null : 0,
        longtext: context.defaultText || article?.longtext,
        shortText: article?.name,
        articleNumber: article?.number,
        customFields: [],
        positionKind: positionKind as any,
        cacheOnly,
        customFieldList: [],
        localId: context.localId,
        firstVisitFocus: true,
      } as ServiceContractPosition;

      return position;
    },
    []
  );

  const mutateState = useCallback(
    (input: ServiceContractPosition[]) => {
      const { total } = recalculatePositions(cloneDeep(input));
      setTotalPrice(total);
      return input;
    },
    [setTotalPrice]
  );

  useEffect(() => {
    if (totalPrice === undefined) {
      const { total } = recalculatePositions(cloneDeep(positions));
      setTotalPrice(total);
    }
  }, [entity, positions, setTotalPrice, totalPrice]);

  const canAddRemote = useCallback(
    (pos: ShadowServiceContractObjectAddComponentArgs) => {
      if (
        pos.typeOfPosition === AddServiceContractPosTypeOfPos.TEXTLINE &&
        pos.article?.id === 0
      )
        return false;
      return true;
    },
    []
  );

  const mapRemoveLocal = useCallback(
    (ids: number[]) => {
      const toRemove = positions
        .filter((pos) => pos && ids.includes(pos.id))
        .map((x) => x.id);

      return toRemove;
    },
    [positions]
  );

  const output = useEditableState<
    ServiceContractPosition,
    ShadowServiceContractObjectAddComponentArgs
  >({
    positions,
    onAddComponent: shadowServiceContractObjectApi.addComponent,
    onEditComponent: shadowServiceContractObjectApi.editComponent,
    onMoveComponent: shadowServiceContractObjectApi.moveComponent,
    onRemoveComponent: shadowServiceContractObjectApi.removeComponent,
    mapAddLocal,
    mapRemoveLocal,
    mutateState,
    canAddRemote,
  });
  const isInCopyMode = useRef(false);

  useEffect(() => {
    const positionIds = positions.map((pos) => pos.id);
    const diff = output.allPositions.filter(
      (pos) => !positionIds.includes(pos.id)
    );
    const currendDif = output.allPositions?.length - positions?.length;
    if (currendDif > 0) {
      const el = diff[diff.length - 1] as EditTableEntry;
      const index = el.index;
      editTableRef?.current?.scrollTo(
        index,
        el.cacheOnly ? 'longtext' : 'amount'
      );
    }
    // scroll to last, timeout is needed to scroll to just added row instead of -1
    if (isInCopyMode.current) {
      setTimeout(() => {
        editTableRef?.current?.scrollTo();
      }, 100);
    }
    isInCopyMode.current = false;
  }, [editTableRef, output.allPositions, positions]);

  const returnPositions = useMemo(() => {
    if (output.positions.some((x) => x.localId === lastCacheId.current)) {
      lastCacheId.current = uuid();
    }
    if (isSimpleMode) {
      return [
        ...output.positions,
        {
          localId: lastCacheId.current,
          cacheOnly: true,
          positionKind: AddServiceContractPosTypeOfPos.TEXTLINE,
        },
      ];
    }

    if (output.positions.length === 0) {
      return [
        {
          localId: lastCacheId.current,
          cacheOnly: true,
          positionKind: AddServiceContractPosTypeOfPos.TEXTLINE,
        },
      ];
    }

    return output.positions;
  }, [isSimpleMode, output.positions]);

  const onRemoveComponent = useCallback(
    async (localIds: IdArray) => {
      const ids = getIdsIncomingKeys(output.positions, localIds);

      const toRemove = output.positions
        .filter((pos) => pos && ids.includes(pos.id))
        .map((pos) => pos.id);

      output.onRemoveComponent(toRemove);
    },
    [output]
  );

  const onMoveComponent = useCallback(
    (localId: string, toIndex: number) => {
      const toItem = output.positions[toIndex] as EditTableEntry;

      if (!toItem) {
        console.warn(
          'onMoveComponent: toItem is null',
          toItem,
          toIndex,
          output.positions
        );
        return;
      }
      return output.onMoveComponent(localId, toItem.index);
    },
    [output]
  );

  const onAddComponent = useCallback(
    (context: ShadowServiceContractObjectAddComponentArgs) => {
      return output.onAddComponent(context);
    },
    [output]
  );

  const onEditComponent = useCallback(
    (context: OnEditComponent<ServiceContractPosition>) => {
      return output.onEditComponent(context);
    },
    [output]
  );

  const onCloneComponent = useCallback(
    (copyComponents: IdArray) => {
      isInCopyMode.current = true;
      const ids = getIdsIncomingKeys(output.positions, copyComponents);
      shadowServiceContractObjectApi.addComponent({
        copyPositions: ids,
      });
    },
    [output, shadowServiceContractObjectApi]
  );

  return {
    ...output,
    onAddComponent,
    onEditComponent,
    onMoveComponent,
    onRemoveComponent,
    onCloneComponent,
    positions: returnPositions as ServiceContractPosition[],
    total: totalPrice,
  };
};
